// shadcn/ui Button primitive. Uses class-variance-authority for variants
// and Radix Slot for the asChild pattern (so a Button can be a Link).

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils.js';

// "Pop out" hover affordance: a subtle lift + navy-tinted shadow on hover that
// presses back down on click. transform-gpu keeps it composited (no reflow);
// disabled buttons opt out via `disabled:pointer-events-none` below. Applied to
// the solid/bordered variants — NOT `link` (a floating text link reads odd).
const pop =
  'transform-gpu hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-2px_rgba(27,42,63,0.18)] active:translate-y-0 active:shadow-sm';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: `bg-primary text-primary-foreground hover:bg-primary/90 ${pop}`,
        destructive: `bg-destructive text-destructive-foreground hover:bg-destructive/90 ${pop}`,
        outline: `border border-input bg-background hover:bg-accent hover:text-accent-foreground ${pop}`,
        secondary: `bg-secondary text-secondary-foreground hover:bg-secondary/80 ${pop}`,
        ghost: `hover:bg-accent hover:text-accent-foreground ${pop}`,
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
